import { FileSpreadsheet, ArrowDownToLine } from "lucide-react";
import MarketingPageLayout from "../components/MarketingPageLayout";
import SeoHelmet from "../components/SeoHelmet";
import MarketingHero from "../components/MarketingHero";
import InternalLinks from "../components/InternalLinks";
import CtaFinal from "../components/CtaFinal";
import { MARKETING_META } from "../seoMeta";
import { FEATURE_SOFTWARE_LD } from "../data/featurePages";
import useTranslation from "../../client/hooks/useTranslation";
import NinjaTraderTrademarkNotice from "../../client/components/legal/NinjaTraderTrademarkNotice";
import PartnerLogo from "../../client/components/legal/PartnerLogo";
import { cn } from "../../client/utils";
import ScrollAnimation from "../../landing-page/components/ScrollAnimation";

const PATH = "/integrations";

// Cycled accent palette — mirrors FeatureCardGrid / the homepage FeaturesGrid.
const ACCENTS = ["#A855F7", "#3D6EFF", "#00C9A7", "#F59E0B", "#EC4899"];

// Logos for the CSV import list, keyed by the (translation) item label.
const CSV_LOGOS: Record<string, string> = {
  Tradovate: "/logos/tradovate.png",
  TopstepX: "/logos/topstepx.png",
};

export default function IntegrationsPage() {
  const t = useTranslation();
  const m = MARKETING_META[PATH];
  const c = t.marketing.features.integrations;

  return (
    <MarketingPageLayout>
      <SeoHelmet
        path={PATH}
        title={m.title}
        description={m.description}
        jsonLd={[FEATURE_SOFTWARE_LD]}
      />
      <MarketingHero h1={c.h1} subtitle={c.subtitle} />

      {/* Section 1 — Automatic sync */}
      <section className="px-6 py-12 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title={c.autoSyncTitle} />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {c.autoSyncCards.map((card, index) => {
              // Card 0 = NinjaTrader & Tradovate → NinjaTrader logo via
              // <PartnerLogo> (18px clear-space, tracked URL) shown next to the
              // Tradovate logo. Card 1 = MetaAPI (MetaTrader logo). Order is
              // fixed by `autoSyncCards` in translations.ts.
              const isNinjaTrader = index === 0;
              return (
                <ScrollAnimation key={card.title} direction="up" delay={index * 60}>
                  <IntegrationCard
                    title={card.title}
                    badge={card.badge}
                    body={card.body}
                    accent={ACCENTS[index % ACCENTS.length]}
                    ninjaTraderLogo={isNinjaTrader}
                    plainLogos={
                      isNinjaTrader
                        ? [{ src: "/logos/tradovate.png", alt: "Tradovate" }]
                        : [{ src: "/logos/metatrader.png", alt: "MetaTrader" }]
                    }
                  />
                </ScrollAnimation>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 2 — CSV imports & copy-paste */}
      <section className="px-6 py-12 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title={c.csvTitle} />
          <ScrollAnimation direction="up">
            <div
              className={cn(
                "group relative overflow-hidden rounded-xl",
                "border border-border/40 bg-card/50 backdrop-blur-sm",
                "p-6",
              )}
            >
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg border"
                style={{ backgroundColor: "#00C9A714", borderColor: "#00C9A730" }}
              >
                <FileSpreadsheet className="h-5 w-5" style={{ color: "#00C9A7" }} />
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {c.csvIntro}
              </p>
              <ul className="mt-4 flex flex-wrap gap-3">
                {c.csvItems.map((item) => {
                  const logoSrc = CSV_LOGOS[item];
                  return (
                    <li
                      key={item}
                      className="flex items-center gap-2 rounded-lg border border-border/60 bg-card/60 px-4 py-3 text-sm font-medium text-foreground"
                    >
                      {logoSrc ? (
                        <img
                          src={logoSrc}
                          alt={item}
                          loading="lazy"
                          className="h-12 w-auto object-contain"
                        />
                      ) : (
                        <>
                          <ArrowDownToLine className="h-4 w-4 text-primary" />
                          {item}
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </ScrollAnimation>
        </div>
      </section>

      <InternalLinks
        links={[
          { to: "/tarifs", label: t.marketing.nav.pricing },
          {
            to: "/journal-de-trading",
            label: t.marketing.nav.megaFeatures[0].label,
          },
          {
            to: "/fonctionnalites/prop-firm-manager",
            label: t.marketing.nav.megaFeatures[1].label,
          },
        ]}
      />

      <CtaFinal />

      {/* NinjaTrader Vendor Program — required because this page mentions NinjaTrader. */}
      <section className="px-6 pb-12 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <NinjaTraderTrademarkNotice />
        </div>
      </section>
    </MarketingPageLayout>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <h2 className="mb-8 font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
      {title}
    </h2>
  );
}

function IntegrationCard({
  title,
  badge,
  body,
  accent,
  ninjaTraderLogo = false,
  plainLogos = [],
}: {
  title: string;
  badge?: string;
  body: string;
  accent: string;
  ninjaTraderLogo?: boolean;
  plainLogos?: { src: string; alt: string }[];
}) {
  return (
    <div
      className={cn(
        "group relative flex h-full min-h-[180px] flex-col overflow-hidden rounded-xl",
        "border border-border/40 bg-card/50 backdrop-blur-sm",
        "cursor-default transition-all duration-300",
        "hover:border-border/80 hover:bg-card/80",
      )}
    >
      {/* HUD corners */}
      <div
        className="pointer-events-none absolute left-0 top-0 h-4 w-4 border-l border-t opacity-40 transition-opacity duration-300 group-hover:opacity-80"
        style={{ borderColor: accent }}
      />
      <div
        className="pointer-events-none absolute bottom-0 right-0 h-4 w-4 border-b border-r opacity-40 transition-opacity duration-300 group-hover:opacity-80"
        style={{ borderColor: accent }}
      />

      {/* Ambient glow on hover */}
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
        style={{ backgroundColor: `${accent}22` }}
      />

      {/* Top accent line */}
      <div
        className="absolute left-0 right-0 top-0 h-px opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background: `linear-gradient(90deg, transparent, ${accent}60, transparent)`,
        }}
      />

      <div className="relative z-10 flex h-full flex-col p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="-ml-[18px] flex flex-wrap items-center gap-x-2 gap-y-2">
            {ninjaTraderLogo && (
              // PartnerLogo keeps the mandatory 18px clear-space on all sides
              // (inline padding) and links to the tracked NinjaTrader URL. The
              // -ml-[18px] on the row pulls that left padding back to the card's
              // inner edge so NinjaTrader lines up with the other logos; the
              // 18px still reads as clear-space toward the card border and the
              // neighbouring logo. The wordmark SVG has no baked-in whitespace,
              // so h-7 already matches the Tradovate mark optically.
              <PartnerLogo
                partner="ninjatrader"
                src="/logos/ninjatrader-wordmark.svg"
                alt="NinjaTrader"
                imgClassName="h-7 w-auto"
              />
            )}
            {plainLogos.map((logo) => (
              <img
                key={logo.src}
                src={logo.src}
                alt={logo.alt}
                loading="lazy"
                className="h-12 w-auto object-contain"
              />
            ))}
          </div>
          {badge && (
            <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
              {badge}
            </span>
          )}
        </div>

        <h3 className="mb-2 text-base font-semibold leading-snug text-foreground">
          {title}
        </h3>
        <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
          {body}
        </p>
      </div>
    </div>
  );
}
